import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { UserContext } from '../contexts/UserContext';

/**
 * Admin-published announcement banner at the top of the logged-in dashboard.
 *
 * Config lives in a single doc, platformConfig/dashboardBanner, written by the
 * Dashboard Banner admin tab. Dismissal is per banner `id` rather than per
 * feature, so publishing a new banner re-surfaces to everyone without needing a
 * new Firestore field each time (the retired referral / multi-pedal banners each
 * needed their own boolean).
 *
 * Everything here fails open to "no banner". An admin hand-types the body, link
 * and image, and this renders for every signed-in user, so a bad value must
 * never be able to take the dashboard down with it: unknown or malformed input
 * is dropped field-by-field in normalizeBannerConfig, a broken image hides
 * itself via onError, and BannerErrorBoundary catches anything that still
 * throws during render.
 */

export const BANNER_CONFIG_PATH = ['platformConfig', 'dashboardBanner'];
export const BANNER_MAX_BODY = 500;
export const BANNER_MAX_LABEL = 60;
export const BANNER_MAX_LINK_TEXT = 60;

const BRAND = '#5A9ECB';

const dismissLsKey = (uid, bannerId) => `dashboardBannerDismissed_${uid}_${bannerId}`;

/**
 * Mirrors the useIsMobile helper in AdminDashboard — no Tailwind here, so layout
 * flips read window width. 768px matches the banner's own media query in
 * UserDashboard so the two never disagree about what "mobile" means.
 */
const useIsWideViewport = (breakpoint = 768) => {
  const [isWide, setIsWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth > breakpoint : true
  );
  useEffect(() => {
    const handler = () => setIsWide(window.innerWidth > breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isWide;
};

/**
 * Accepts absolute http(s) URLs and site-relative paths only. Returns null for
 * anything else so the caller can omit the element rather than emit an
 * attacker- or typo-controlled href (javascript:, data:, bare text, ...).
 */
export function safeBannerUrl(raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value) return null;

  // Site-relative. Reject protocol-relative "//host" — it leaves the origin.
  if (value.startsWith('/')) {
    return value.startsWith('//') ? null : value;
  }

  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.href;
  } catch (e) {
    return null;
  }
}

const trimTo = (raw, maxLen) => {
  const value = typeof raw === 'string' ? raw.trim() : '';
  return value.length > maxLen ? value.slice(0, maxLen) : value;
};

/**
 * Turns a raw Firestore doc into something safe to render, or null if it should
 * not render at all. Shared with the admin preview so what an admin sees before
 * publishing is produced by the same code path users get.
 */
export function normalizeBannerConfig(raw) {
  try {
    if (!raw || raw.active !== true) return null;

    const id = trimTo(raw.id, 100);
    const body = trimTo(raw.body, BANNER_MAX_BODY);
    if (!id || !body) return null;

    const linkUrl = safeBannerUrl(raw.linkUrl);
    const linkText = trimTo(raw.linkText, BANNER_MAX_LINK_TEXT);

    return {
      id,
      body,
      label: trimTo(raw.label, BANNER_MAX_LABEL),
      linkUrl,
      // A valid link with no label still needs something clickable.
      linkText: linkUrl ? (linkText || 'Learn more') : '',
      imageUrl: safeBannerUrl(raw.imageUrl),
    };
  } catch (e) {
    console.warn('DashboardAnnouncementBanner: unusable banner config', e?.message || e);
    return null;
  }
}

class BannerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('DashboardAnnouncementBanner: render failed, hiding banner', error?.message || error);
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/**
 * Presentational card. Exported so the admin tab previews the real thing.
 * `onDismiss` omitted renders without the ✕ (preview mode).
 */
export const DashboardBannerCard = ({ banner, onDismiss }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const isWide = useIsWideViewport();

  if (!banner) return null;

  const showImage = !!banner.imageUrl && !imageFailed;
  const imageSize = isWide ? 72 : 48;
  const isInternalLink = !!banner.linkUrl && banner.linkUrl.startsWith('/');

  const linkStyle = {
    color: BRAND,
    textDecoration: 'underline',
    fontWeight: '600',
    fontFamily: 'Nunito, sans-serif',
    fontSize: '0.9rem',
  };

  return (
    <div
      className="dashboard-new-feature-banner"
      style={{
        maxWidth: '1200px',
        margin: '1rem auto 0 auto',
        padding: '0 1.5rem',
      }}
    >
      <div style={{
        background: 'rgba(90, 158, 203, 0.12)',
        border: `1px solid ${BRAND}`,
        borderRadius: '12px',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: '12px',
      }}>
        {/* Label / body / link each on their own line. textAlign is set
            explicitly because the dashboard wrapper centers text. */}
        <div style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          textAlign: 'left',
        }}>
          {banner.label && (
            <div style={{
              fontWeight: '700',
              color: BRAND,
              fontSize: '0.95rem',
              fontFamily: 'Nunito, sans-serif',
            }}>
              {banner.label}
            </div>
          )}
          <div style={{
            color: '#4a4a4a',
            fontSize: '0.9rem',
            lineHeight: 1.45,
            fontFamily: 'Nunito, sans-serif',
          }}>
            {banner.body}
          </div>
          {banner.linkUrl && (
            <div>
              {isInternalLink ? (
                <Link to={banner.linkUrl} style={linkStyle}>{banner.linkText}</Link>
              ) : (
                <a
                  href={banner.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  {banner.linkText}
                </a>
              )}
            </div>
          )}
        </div>
        {/* Narrow: ✕ in the top-right corner with the thumbnail centered in the
            space below it. Wide: that same ~72px of vertical space goes to one
            larger thumbnail sitting left of the ✕, so a small image doesn't look
            stranded across a 1200px card. row-reverse paints image-then-✕ while
            keeping the ✕ first in DOM order for keyboard users. Negative margins
            pull the ✕ into the corner past the card's own padding. */}
        <div style={{
          display: 'flex',
          flexDirection: isWide ? 'row-reverse' : 'column',
          alignItems: isWide ? 'center' : 'flex-end',
          gap: isWide ? '14px' : '4px',
          flexShrink: 0,
        }}>
          {onDismiss && (
            <button
              onClick={onDismiss}
              style={{
                background: 'none',
                border: 'none',
                color: '#4a4a4a',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '4px',
                marginTop: '-6px',
                marginRight: '-12px',
                lineHeight: 1,
                opacity: 0.7,
                alignSelf: isWide ? 'flex-start' : undefined,
              }}
              title="Dismiss"
            >
              ✕
            </button>
          )}
          {showImage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              ...(isWide ? {} : { flex: 1, marginRight: '-8px' }),
            }}>
              <img
                src={banner.imageUrl}
                alt=""
                onError={() => setImageFailed(true)}
                style={{
                  width: `${imageSize}px`,
                  height: `${imageSize}px`,
                  objectFit: 'cover',
                  borderRadius: '8px',
                  flexShrink: 0,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DashboardAnnouncementBanner = ({ user }) => {
  const { firestoreUser } = useContext(UserContext);
  const [banner, setBanner] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const uid = user?.uid;

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, ...BANNER_CONFIG_PATH),
      (snap) => {
        setBanner(snap.exists() ? normalizeBannerConfig(snap.data()) : null);
        setConfigLoaded(true);
      },
      (err) => {
        // Missing doc, rules change, offline — no banner, dashboard unaffected.
        console.warn('DashboardAnnouncementBanner: config read failed', err?.message || err);
        setBanner(null);
        setConfigLoaded(true);
      }
    );
    return () => unsub();
  }, []);

  const bannerId = banner?.id;

  // Local dismissal is checked first so the banner does not flash on a fresh
  // load before the user doc arrives. Firestore is the cross-device source.
  useEffect(() => {
    if (!uid || !bannerId) {
      setDismissed(false);
      return;
    }
    let isDismissed = false;
    try {
      isDismissed = localStorage.getItem(dismissLsKey(uid, bannerId)) === 'true';
    } catch (e) { /* private mode / storage disabled — fall back to Firestore */ }

    const ids = firestoreUser?.dismissedDashboardBannerIds;
    if (Array.isArray(ids) && ids.includes(bannerId)) isDismissed = true;

    setDismissed(isDismissed);
  }, [uid, bannerId, firestoreUser?.dismissedDashboardBannerIds]);

  const handleDismiss = async () => {
    if (!uid || !bannerId) return;
    setDismissed(true);
    try {
      localStorage.setItem(dismissLsKey(uid, bannerId), 'true');
    } catch (e) { /* non-fatal — Firestore write below still persists it */ }
    try {
      await updateDoc(doc(db, 'users', uid), {
        dismissedDashboardBannerIds: arrayUnion(bannerId),
      });
    } catch (e) {
      console.warn('Could not persist banner dismiss to profile:', e?.message || e);
    }
  };

  if (!uid || !configLoaded || !banner || dismissed) return null;

  return (
    <BannerErrorBoundary>
      <DashboardBannerCard banner={banner} onDismiss={handleDismiss} />
    </BannerErrorBoundary>
  );
};

export default DashboardAnnouncementBanner;
